import React, { useState, useEffect, useCallback } from 'react';

const DAY_NAMES = {
  '0': 'Segunda', '1': 'Terça', '2': 'Quarta', '3': 'Quinta',
  '4': 'Sexta', '5': 'Sábado', '6': 'Domingo'
};

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let m = 7 * 60; m <= 22 * 60; m += 15) {
    opts.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  return opts;
})();

const S = {
  panel: { marginBottom: '20px', padding: '16px 20px', background: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' },
  title: { margin: 0, color: '#daa520', fontSize: '1rem' },
  tabBtn: (active) => ({
    padding: '8px 14px', background: active ? '#daa520' : '#0a0a0a', color: active ? '#000' : '#ccc',
    border: '1px solid #333', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
  }),
  select: { padding: '6px 8px', background: '#0a0a0a', border: '1px solid #333', borderRadius: '4px', color: '#fff', fontSize: '0.85rem' },
  input: { padding: '8px 10px', background: '#0a0a0a', border: '1px solid #333', borderRadius: '4px', color: '#fff', fontSize: '0.9rem' },
  goldBtn: { padding: '10px 20px', background: '#daa520', color: '#000', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  delBtn: { background: 'transparent', border: 'none', color: '#ff6666', cursor: 'pointer', fontSize: '1.1rem', padding: '0 4px', lineHeight: 1 },
  dayCard: { padding: '12px', background: '#111', border: '1px solid #2a2a2a', borderRadius: '6px', marginBottom: '10px' },
  label: { color: '#aaa', fontSize: '0.78rem', display: 'block', marginBottom: '3px' },
  itemCard: { padding: '10px 14px', background: '#111', border: '1px solid #2a2a2a', borderRadius: '6px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  hint: { margin: '10px 0 0', fontSize: '0.78rem', color: '#888', fontStyle: 'italic' }
};

function TimeSelect({ value, onChange, allowEmpty, emptyLabel, testId }) {
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} style={S.select} data-testid={testId}>
      {allowEmpty && <option value="">{emptyLabel || '—'}</option>}
      {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );
}

export default function ScheduleManager({ backendUrl }) {
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState('weekly');
  const [weekly, setWeekly] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [excForm, setExcForm] = useState({ label: '', start_date: '', end_date: '', morning_end: '', afternoon_end: '' });
  const [ovForm, setOvForm] = useState({ date: '', label: '', no_morning: false, no_afternoon: false, morning_start: '', morning_end: '', afternoon_start: '', afternoon_end: '', capacity: '', hourly_only: false });
  const [previewDate, setPreviewDate] = useState('');
  const [preview, setPreview] = useState(null);

  const flash = (text, isError = false) => {
    setMsg({ text, isError });
    setTimeout(() => setMsg(null), 4000);
  };

  const fetchAll = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/schedule`);
      const data = await res.json();
      setWeekly(data.weekly);
      setExceptions(data.exceptions || []);
      setOverrides(data.date_overrides || []);
    } catch (e) {
      flash('Erro ao carregar a configuração', true);
    }
  }, [backendUrl]);

  useEffect(() => {
    if (expanded && !weekly) fetchAll();
  }, [expanded, weekly, fetchAll]);

  const updateDay = (k, field, value) => {
    setWeekly((w) => ({ ...w, [k]: { ...w[k], [field]: value } }));
  };
  const updateDouble = (k, field, value) => {
    setWeekly((w) => ({ ...w, [k]: { ...w[k], double: { ...w[k].double, [field]: value } } }));
  };

  const saveWeekly = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/schedule/weekly`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: weekly })
      });
      const data = await res.json();
      if (res.ok) flash('✅ Horários semanais guardados — ativos imediatamente');
      else flash(`❌ ${data.detail || 'Erro ao guardar'}`, true);
    } catch (e) {
      flash('❌ Erro de rede', true);
    }
    setSaving(false);
  };

  const addException = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/schedule/exceptions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(excForm)
      });
      const data = await res.json();
      if (res.ok) {
        setExcForm({ label: '', start_date: '', end_date: '', morning_end: '', afternoon_end: '' });
        flash('✅ Exceção adicionada');
        fetchAll();
      } else flash(`❌ ${data.detail || 'Erro'}`, true);
    } catch (e) { flash('❌ Erro de rede', true); }
  };

  const deleteException = async (id) => {
    const res = await fetch(`${backendUrl}/api/admin/schedule/exceptions/${id}`, { method: 'DELETE' });
    flash(res.ok ? 'Exceção removida' : '❌ Erro ao remover', !res.ok);
    fetchAll();
  };

  const addOverride = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/schedule/date-overrides`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ovForm)
      });
      const data = await res.json();
      if (res.ok) {
        setOvForm({ date: '', label: '', no_morning: false, no_afternoon: false, morning_start: '', morning_end: '', afternoon_start: '', afternoon_end: '', capacity: '', hourly_only: false });
        flash('✅ Horário especial guardado');
        fetchAll();
      } else flash(`❌ ${data.detail || 'Erro'}`, true);
    } catch (e) { flash('❌ Erro de rede', true); }
  };

  const deleteOverride = async (date) => {
    const res = await fetch(`${backendUrl}/api/admin/schedule/date-overrides/${date}`, { method: 'DELETE' });
    flash(res.ok ? 'Horário especial removido' : '❌ Erro ao remover', !res.ok);
    fetchAll();
  };

  const loadPreview = async () => {
    if (!previewDate) return;
    try {
      const res = await fetch(`${backendUrl}/api/admin/schedule/preview/${previewDate}`);
      setPreview(await res.json());
    } catch (e) { flash('❌ Erro ao carregar pré-visualização', true); }
  };

  return (
    <div style={S.panel} data-testid="schedule-manager-panel">
      <div style={S.header} onClick={() => setExpanded(!expanded)} data-testid="toggle-schedule-manager">
        <h3 style={S.title}>
          🗓️ Gestão do Calendário
          <span style={{ marginLeft: '10px', fontSize: '0.85rem', color: '#888', fontWeight: 'normal' }}>
            (horários, capacidade, exceções)
          </span>
        </h3>
        <span style={{ color: '#daa520', fontSize: '1.2rem' }}>{expanded ? '▼' : '▶'}</span>
      </div>

      {expanded && (
        <div style={{ marginTop: '16px' }}>
          {msg && (
            <div data-testid="schedule-manager-message" style={{
              padding: '10px 14px', marginBottom: '12px', borderRadius: '4px', fontSize: '0.9rem',
              background: msg.isError ? '#2a1a1a' : '#1a2a1a',
              border: `1px solid ${msg.isError ? '#aa4444' : '#44aa44'}`, color: '#fff'
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <button type="button" style={S.tabBtn(tab === 'weekly')} onClick={() => setTab('weekly')} data-testid="schedule-tab-weekly">Horários Semanais</button>
            <button type="button" style={S.tabBtn(tab === 'exceptions')} onClick={() => setTab('exceptions')} data-testid="schedule-tab-exceptions">Exceções Temporárias</button>
            <button type="button" style={S.tabBtn(tab === 'overrides')} onClick={() => setTab('overrides')} data-testid="schedule-tab-overrides">Dias Específicos</button>
            <button type="button" style={S.tabBtn(tab === 'preview')} onClick={() => setTab('preview')} data-testid="schedule-tab-preview">Pré-visualizar</button>
          </div>

          {!weekly ? <p style={{ color: '#888' }}>A carregar…</p> : (
            <>
              {tab === 'weekly' && (
                <div data-testid="schedule-weekly-section">
                  {Object.keys(DAY_NAMES).map((k) => {
                    const d = weekly[k];
                    return (
                      <div key={k} style={S.dayCard} data-testid={`schedule-day-${k}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: d.open ? '10px' : 0, flexWrap: 'wrap' }}>
                          <strong style={{ color: '#daa520', width: '80px' }}>{DAY_NAMES[k]}</strong>
                          <label style={{ color: '#ccc', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={d.open} onChange={(e) => updateDay(k, 'open', e.target.checked)} data-testid={`schedule-day-${k}-open`} />
                            Aberto
                          </label>
                        </div>
                        {d.open && (
                          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div>
                              <span style={S.label}>Intervalo</span>
                              <select
                                value={d.interval || 30}
                                onChange={(e) => updateDay(k, 'interval', parseInt(e.target.value, 10))}
                                style={S.select}
                                data-testid={`schedule-day-${k}-interval`}
                              >
                                {[15, 30, 45, 60].map((v) => <option key={v} value={v}>{v} min</option>)}
                              </select>
                            </div>
                            <div>
                              <span style={S.label}>Manhã (1.º → último RDV)</span>
                              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <TimeSelect value={d.morning_start} onChange={(v) => updateDay(k, 'morning_start', v)} testId={`schedule-day-${k}-morning-start`} />
                                <span style={{ color: '#888' }}>→</span>
                                <TimeSelect value={d.morning_end} onChange={(v) => updateDay(k, 'morning_end', v)} testId={`schedule-day-${k}-morning-end`} />
                              </div>
                            </div>
                            <div>
                              <span style={S.label}>Tarde (1.º → último RDV)</span>
                              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <TimeSelect value={d.afternoon_start} onChange={(v) => updateDay(k, 'afternoon_start', v)} testId={`schedule-day-${k}-afternoon-start`} />
                                <span style={{ color: '#888' }}>→</span>
                                <TimeSelect value={d.afternoon_end} onChange={(v) => updateDay(k, 'afternoon_end', v)} testId={`schedule-day-${k}-afternoon-end`} />
                              </div>
                            </div>
                            <div>
                              <label style={{ ...S.label, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginBottom: '6px' }}>
                                <input type="checkbox" checked={d.double.enabled} onChange={(e) => updateDouble(k, 'enabled', e.target.checked)} data-testid={`schedule-day-${k}-double-enabled`} />
                                2 RDV por horário
                              </label>
                              {d.double.enabled && (
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                  <TimeSelect value={d.double.start} onChange={(v) => updateDouble(k, 'start', v)} testId={`schedule-day-${k}-double-start`} />
                                  <span style={{ color: '#888' }}>→</span>
                                  <TimeSelect value={d.double.end} onChange={(v) => updateDouble(k, 'end', v)} testId={`schedule-day-${k}-double-end`} />
                                  <label style={{ color: '#aaa', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={d.double.full_hours_only} onChange={(e) => updateDouble(k, 'full_hours_only', e.target.checked)} data-testid={`schedule-day-${k}-full-hours-only`} />
                                    só horas certas (XX:00)
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button type="button" onClick={saveWeekly} disabled={saving} style={{ ...S.goldBtn, opacity: saving ? 0.6 : 1 }} data-testid="save-weekly-schedule-btn">
                    {saving ? 'A guardar…' : '💾 Guardar horários semanais'}
                  </button>
                  <p style={S.hint}>💡 As alterações aplicam-se imediatamente às novas reservas. As marcações já feitas nunca são alteradas.</p>
                </div>
              )}

              {tab === 'exceptions' && (
                <div data-testid="schedule-exceptions-section">
                  <div style={{ ...S.dayCard, display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '1', minWidth: '160px' }}>
                      <span style={S.label}>Descrição (opcional)</span>
                      <input type="text" value={excForm.label} onChange={(e) => setExcForm({ ...excForm, label: e.target.value })} style={{ ...S.input, width: '100%', boxSizing: 'border-box' }} placeholder="Ex: Agosto — manhã até às 11h30" data-testid="exception-label-input" />
                    </div>
                    <div>
                      <span style={S.label}>De</span>
                      <input type="date" value={excForm.start_date} onChange={(e) => setExcForm({ ...excForm, start_date: e.target.value })} style={S.input} data-testid="exception-start-input" />
                    </div>
                    <div>
                      <span style={S.label}>Até</span>
                      <input type="date" value={excForm.end_date} onChange={(e) => setExcForm({ ...excForm, end_date: e.target.value })} style={S.input} data-testid="exception-end-input" />
                    </div>
                    <div>
                      <span style={S.label}>Fim da manhã</span>
                      <TimeSelect value={excForm.morning_end} onChange={(v) => setExcForm({ ...excForm, morning_end: v })} allowEmpty emptyLabel="— sem limite" testId="exception-morning-end-select" />
                    </div>
                    <div>
                      <span style={S.label}>Fim do dia</span>
                      <TimeSelect value={excForm.afternoon_end} onChange={(v) => setExcForm({ ...excForm, afternoon_end: v })} allowEmpty emptyLabel="— sem limite" testId="exception-afternoon-end-select" />
                    </div>
                    <button type="button" onClick={addException} style={S.goldBtn} data-testid="add-exception-btn">+ Adicionar</button>
                  </div>
                  {exceptions.length === 0 ? (
                    <p style={{ color: '#888', fontStyle: 'italic' }}>Nenhuma exceção ativa</p>
                  ) : exceptions.map((exc) => (
                    <div key={exc.id} style={S.itemCard} data-testid={`exception-item-${exc.id}`}>
                      <div style={{ color: '#fff', fontSize: '0.9rem' }}>
                        <strong style={{ color: '#daa520' }}>{exc.label}</strong>
                        <div style={{ color: '#aaa', fontSize: '0.82rem', marginTop: '2px' }}>
                          📅 {exc.start_date} → {exc.end_date}
                          {exc.morning_end && <span> · manhã até às {exc.morning_end}</span>}
                          {exc.afternoon_end && <span> · dia até às {exc.afternoon_end}</span>}
                        </div>
                      </div>
                      <button type="button" onClick={() => deleteException(exc.id)} style={S.delBtn} title="Remover" data-testid={`delete-exception-${exc.id}`}>✕</button>
                    </div>
                  ))}
                  <p style={S.hint}>💡 Durante o período, a manhã e/ou o dia terminam mais cedo. Fora do período, tudo volta ao normal automaticamente.</p>
                </div>
              )}

              {tab === 'overrides' && (
                <div data-testid="schedule-overrides-section">
                  <div style={S.dayCard}>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '10px' }}>
                      <div>
                        <span style={S.label}>Data</span>
                        <input type="date" value={ovForm.date} onChange={(e) => setOvForm({ ...ovForm, date: e.target.value })} style={S.input} data-testid="override-date-input" />
                      </div>
                      <div style={{ flex: '1', minWidth: '150px' }}>
                        <span style={S.label}>Descrição (opcional)</span>
                        <input type="text" value={ovForm.label} onChange={(e) => setOvForm({ ...ovForm, label: e.target.value })} style={{ ...S.input, width: '100%', boxSizing: 'border-box' }} placeholder="Ex: domingo especial" data-testid="override-label-input" />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '10px' }}>
                      <div>
                        <span style={S.label}>Manhã (vazio = horário normal)</span>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <TimeSelect value={ovForm.morning_start} onChange={(v) => setOvForm({ ...ovForm, morning_start: v })} allowEmpty testId="override-morning-start" />
                          <span style={{ color: '#888' }}>→</span>
                          <TimeSelect value={ovForm.morning_end} onChange={(v) => setOvForm({ ...ovForm, morning_end: v })} allowEmpty testId="override-morning-end" />
                          <label style={{ color: '#aaa', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={ovForm.no_morning} onChange={(e) => setOvForm({ ...ovForm, no_morning: e.target.checked })} data-testid="override-no-morning" />
                            fechado de manhã
                          </label>
                        </div>
                      </div>
                      <div>
                        <span style={S.label}>Tarde (vazio = horário normal)</span>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <TimeSelect value={ovForm.afternoon_start} onChange={(v) => setOvForm({ ...ovForm, afternoon_start: v })} allowEmpty testId="override-afternoon-start" />
                          <span style={{ color: '#888' }}>→</span>
                          <TimeSelect value={ovForm.afternoon_end} onChange={(v) => setOvForm({ ...ovForm, afternoon_end: v })} allowEmpty testId="override-afternoon-end" />
                          <label style={{ color: '#aaa', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={ovForm.no_afternoon} onChange={(e) => setOvForm({ ...ovForm, no_afternoon: e.target.checked })} data-testid="override-no-afternoon" />
                            fechado à tarde
                          </label>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div>
                        <span style={S.label}>Capacidade por horário</span>
                        <select value={ovForm.capacity} onChange={(e) => setOvForm({ ...ovForm, capacity: e.target.value })} style={S.select} data-testid="override-capacity-select">
                          <option value="">Normal (segundo o dia)</option>
                          <option value="1">1 RDV por horário</option>
                          <option value="2">2 RDV por horário</option>
                        </select>
                      </div>
                      <label style={{ color: '#ccc', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={ovForm.hourly_only} onChange={(e) => setOvForm({ ...ovForm, hourly_only: e.target.checked })} data-testid="override-hourly-only" />
                        Apenas horas certas (10:00, 11:00…)
                      </label>
                      <button type="button" onClick={addOverride} style={S.goldBtn} data-testid="add-override-btn">+ Guardar dia especial</button>
                    </div>
                  </div>
                  {overrides.length === 0 ? (
                    <p style={{ color: '#888', fontStyle: 'italic' }}>Nenhum dia específico configurado</p>
                  ) : overrides.map((ov) => (
                    <div key={ov.date} style={S.itemCard} data-testid={`override-item-${ov.date}`}>
                      <div style={{ color: '#fff', fontSize: '0.9rem' }}>
                        <strong style={{ color: '#daa520' }}>📅 {ov.date}</strong>{ov.label ? ` — ${ov.label}` : ''}
                        <div style={{ color: '#aaa', fontSize: '0.82rem', marginTop: '2px' }}>
                          {ov.no_morning && 'fechado de manhã · '}
                          {ov.no_afternoon && 'fechado à tarde · '}
                          {(ov.morning_start || ov.morning_end) && `manhã ${ov.morning_start || '(normal)'} → ${ov.morning_end || '(normal)'} · `}
                          {(ov.afternoon_start || ov.afternoon_end) && `tarde ${ov.afternoon_start || '(normal)'} → ${ov.afternoon_end || '(normal)'} · `}
                          {ov.capacity && `${ov.capacity} RDV/horário · `}
                          {ov.hourly_only && 'apenas horas certas'}
                        </div>
                      </div>
                      <button type="button" onClick={() => deleteOverride(ov.date)} style={S.delBtn} title="Remover" data-testid={`delete-override-${ov.date}`}>✕</button>
                    </div>
                  ))}
                  <p style={S.hint}>💡 Para fechar um dia inteiro, use o painel «Datas de Fechamento» acima.</p>
                </div>
              )}

              {tab === 'preview' && (
                <div data-testid="schedule-preview-section">
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
                    <input type="date" value={previewDate} onChange={(e) => setPreviewDate(e.target.value)} style={S.input} data-testid="preview-date-input" />
                    <button type="button" onClick={loadPreview} style={S.goldBtn} data-testid="preview-load-btn">Ver horários</button>
                  </div>
                  {preview && (
                    <div data-testid="preview-result">
                      {preview.closed ? (
                        <p style={{ color: '#ff6666' }}>🚫 Salão fechado em {preview.date}</p>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {preview.slots.map((s) => (
                            <span key={s.time} style={{
                              padding: '6px 10px', borderRadius: '4px', fontSize: '0.85rem',
                              background: s.capacity === 2 ? '#1a2a1a' : '#111',
                              border: `1px solid ${s.capacity === 2 ? '#44aa44' : '#333'}`, color: '#fff'
                            }}>
                              {s.time} <span style={{ color: '#888' }}>×{s.capacity}</span>
                            </span>
                          ))}
                        </div>
                      )}
                      <p style={S.hint}>×1 = 1 RDV possível · ×2 = 2 RDV possíveis nesse horário (sem contar reservas já feitas)</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
